import { installTouchLoop, repeatBuffer } from './rr-featured-touch.js?v=20260918-native-loop-2';
export const wrapIndex = (index, count) => ((index % count) + count) % count;
export const slideEase = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
export const horizontalWheelIntent = e => Boolean(e.shiftKey && e.deltaY) || Math.abs(e.deltaX) > Math.abs(e.deltaY);

if (typeof customElements !== 'undefined' && !customElements.get('rr-featured-loop')) {
  class FeaturedLoop extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.originals = [...this.children];
      this.index = 0;
      this.timer = null;
      this.frame = null;
      this.settle = null;
      this.visible = false;
      this.resumeAt = 0;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.shell = this.closest('.floating-controls-container');
      this.block = this.closest('.featured-collections-carousel__item') || this.shell;
      const listen = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal: this.abort.signal });
      for (const event of ['pointerdown', 'pointerup', 'focusin', 'keydown', 'click']) {
        listen(this.block, event, () => this.interact(), { passive: true });
      }
      listen(this.block, 'pointermove', e => {
        if (e.movementX || e.movementY) this.interact();
      }, { passive: true });
      listen(document, 'visibilitychange', () => { if (document.hidden) this.cancel(); this.schedule(); });
      listen(this.reduced, 'change', () => { this.cancel(); this.schedule(); });
      listen(this, 'scroll', () => {
        this.updateDepth();
        if (this.frame !== null) return;
        const rebased = this.rebasedPosition !== undefined && Math.abs(this.position - this.rebasedPosition) < 1;
        this.rebasedPosition = undefined;
        this.normalize();
        if (!rebased) {
          if (performance.now() < (this.ignoreWheelScrollUntil || 0)) this.ignoreWheelScrollUntil = performance.now() + 500;
          else { this.interact(); this.queueSnap(); }
        }
      }, { passive: true });
      listen(this, 'wheel', e => {
        if (horizontalWheelIntent(e)) {
          this.ignoreWheelScrollUntil = 0;
          this.cancel(); this.interact();
        } else {
          this.ignoreWheelScrollUntil = performance.now() + 500;
        }
      }, { passive: true });
      listen(this, 'pointerdown', e => {
        if (e.pointerType === 'touch' && window.innerWidth < 1000) return;
        this.ignoreWheelScrollUntil = 0;
        this.cancel(); this.drag = { x: e.clientX, left: this.scrollLeft, moved: false, mouse: e.pointerType === 'mouse' };
        this.interact();
      });
      listen(window, 'pointermove', e => {
        if (!this.drag?.mouse) return;
        const distance = e.clientX - this.drag.x;
        if (Math.abs(distance) > 5) this.drag.moved = true;
        if (this.drag.moved) { e.preventDefault(); this.scrollLeft = this.drag.left - distance; this.normalize(); this.interact(); }
      }, { passive: false });
      const release = () => { if (!this.drag || this.drag.touch) return; this.suppressClick = this.drag.moved; this.drag = null; this.normalize(); this.interact(); this.queueSnap(); };
      listen(window, 'pointerup', release);
      listen(window, 'pointercancel', release);
      installTouchLoop(this);
      listen(this, 'dragstart', e => e.preventDefault());
      listen(this, 'click', e => { if (this.suppressClick) { e.preventDefault(); this.suppressClick = false; } }, { capture: true });
      this.resize = new ResizeObserver(() => { if (this.visible) this.build(); });
      this.resize.observe(this);
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.build(); else this.cancel();
        this.schedule();
      }, { threshold: 0.1 });
      this.intersection.observe(this);
    }

    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.resize?.disconnect(); this.intersection?.disconnect();
      this.cancel(); clearTimeout(this.timer); clearTimeout(this.settle);
      this.querySelectorAll(':scope > [data-loop-copy]').forEach(e => e.remove());
      this.removeAttribute('data-loop-ready');
    }

    get sign() { return getComputedStyle(this).direction === 'rtl' ? -1 : 1; }
    get position() { return this.scrollLeft * this.sign; }
    set position(value) { this.scrollLeft = value * this.sign; this.updateDepth(); }

    updateDepth() {
      if (!this.cards || !this.pitch) return;
      const position = this.position;
      const midpoint = this.clientWidth / 2;
      this.cards.forEach((card, i) => {
        const offset = i * this.pitch - position + this.cardWidth / 2 - midpoint;
        const distance = Math.abs(offset);
        const scale = (1 - 0.0976 * Math.min(distance / this.pitch, 1)).toFixed(4);
        if (card.style.getPropertyValue('--rr-piece-scale') !== scale) card.style.setProperty('--rr-piece-scale', scale);
        const gap = this.pitch - this.cardWidth;
        const extraSpace = Math.max(0, (gap + this.cardWidth * 0.03) * 1.44 * 1.33 - (gap + this.cardWidth * 0.0488)) + 20;
        const shift = (Math.max(-1, Math.min(offset / this.pitch, 1)) * extraSpace * this.sign).toFixed(2) + 'px';
        if (card.style.getPropertyValue('--rr-piece-shift') !== shift) card.style.setProperty('--rr-piece-shift', shift);
        card.toggleAttribute('data-centered-piece', distance < 1);
        if (distance < 1 && this.labelCard !== card) {
          this.labelCard = card;
          const info = card.querySelector('.product-card__info');
          if (info) {
            const rect = info.getBoundingClientRect();
            this.shell.style.setProperty('--rr-label-center', `${rect.top - this.shell.getBoundingClientRect().top + rect.height / 2}px`);
          }
        }
      });
    }

    copy(card, serial) {
      const clone = card.cloneNode(true);
      clone.dataset.loopCopy = '';
      clone.removeAttribute('reveal-on-scroll');
      clone.style.opacity = '1'; clone.style.transform = 'none';
      clone.setAttribute('aria-hidden', 'true');
      const ids = new Map();
      for (const el of [clone, ...clone.querySelectorAll('[id]')]) {
        if (el.id) { const id = `${this.id}-repeat-${serial}-${el.id}`; ids.set(el.id, id); el.id = id; }
      }
      for (const el of clone.querySelectorAll('*')) {
        for (const attr of ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'list']) {
          if (el.hasAttribute(attr)) el.setAttribute(attr, el.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
        }
        if (el.matches('a,button,input,select,textarea,[tabindex]')) el.setAttribute('tabindex', '-1');
        if (el.matches('img')) { el.loading = window.innerWidth < 1000 ? 'lazy' : 'eager'; el.removeAttribute('fetchpriority'); }
      }
      return clone;
    }

    build() {
      if (!this.originals.length || !this.clientWidth) return;
      const width = this.originals[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
      const pitch = width + gap;
      const info = this.originals[0].querySelector('.product-card__info');
      if (info) {
        const rect = info.getBoundingClientRect();
        this.shell.style.setProperty('--rr-label-center', `${rect.top - this.shell.getBoundingClientRect().top + rect.height / 2}px`);
        const labelWidth = Math.max(...this.originals.map(card => Math.max(card.querySelector('.product-title')?.getBoundingClientRect().width || 0, card.querySelector('price-list')?.getBoundingClientRect().width || 0)));
        this.shell.style.setProperty('--rr-label-half-width', `${Math.min(labelWidth / 2, Math.max(0, this.clientWidth / 2 - 70))}px`);
      }
      if (!width || (pitch === this.pitch && this.clientWidth === this.lastWidth)) return;
      this.cancel();
      this.pitch = pitch; this.lastWidth = this.clientWidth;
      this.cardWidth = width;
      this.peek = (this.clientWidth - width) / 2;
      this.querySelectorAll(':scope > [data-loop-copy]').forEach(e => e.remove());
      this.looping = this.originals.length * pitch > this.clientWidth + gap + 1;
      this.classList.toggle('is-scrollable', this.looping);
      this.shell.querySelectorAll('carousel-prev-button button,carousel-next-button button').forEach(b => { b.disabled = !this.looping; });
      if (!this.looping) { this.position = 0; this.schedule(); return; }
      this.buffer = repeatBuffer(this.clientWidth, pitch, window.innerWidth);
      const before = document.createDocumentFragment(), after = document.createDocumentFragment();
      for (let i = 0; i < this.buffer; i++) {
        before.append(this.copy(this.originals[wrapIndex(i - this.buffer, this.originals.length)], `before-${i}`));
        after.append(this.copy(this.originals[i % this.originals.length], `after-${i}`));
      }
      this.prepend(before); this.append(after);
      this.cards = [...this.children];
      this.setAttribute('data-loop-ready', '');
      this.position = (this.buffer + this.index) * this.pitch - this.peek;
      this.rebasedPosition = this.position;
      this.schedule();
    }

    normalize() {
      if (this.nativeTouchScrolling) return;
      if (!this.looping || this.frame !== null) return;
      const relative = this.position - (this.buffer * this.pitch - this.peek);
      const cycle = this.originals.length * this.pitch;
      const normalized = ((relative % cycle) + cycle) % cycle;
      if (Math.abs(relative - normalized) > 1) {
        const before = this.scrollLeft;
        this.position = this.buffer * this.pitch - this.peek + normalized;
        this.rebasedPosition = this.position;
        if (this.drag) this.drag.left += this.scrollLeft - before;
      }
      this.index = wrapIndex(Math.round(normalized / this.pitch), this.originals.length);
      this.dataset.loopIndex = this.index;
    }

    cancel() {
      this.stopTouchMomentum?.();
      clearTimeout(this.settle);
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    queueSnap() {
      clearTimeout(this.settle);
      if (!this.drag) this.settle = setTimeout(() => this.move(0), 80);
    }

    previous() { this.interact(); this.move(-1); }
    next() { this.interact(); this.move(1); }

    move(step) {
      if (!this.looping || this.frame !== null || this.drag) return;
      clearTimeout(this.settle); this.normalize();
      const from = this.position;
      const origin = this.buffer * this.pitch - this.peek;
      const target = origin + (Math.round((from - origin) / this.pitch) + step) * this.pitch;
      const duration = this.reduced.matches ? 0 : step === 0 ? 220 : 1200;
      clearTimeout(this.timer); this.timer = null;
      const start = performance.now();
      const tick = now => {
        const progress = duration ? Math.min((now - start) / duration, 1) : 1;
        const eased = step === 0 ? 1 - Math.pow(1 - progress, 3) : slideEase(progress);
        this.position = from + (target - from) * eased;
        if (progress < 1) this.frame = requestAnimationFrame(tick);
        else { this.frame = null; this.normalize(); this.rebasedPosition = this.position; this.schedule(); }
      };
      this.frame = requestAnimationFrame(tick);
    }

    interact() {
      this.resumeAt = performance.now() + 5000;
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer); this.timer = null;
      if (!this.abort || !this.visible || !this.looping || this.frame !== null || this.drag || this.reduced.matches || document.hidden || window.Shopify?.designMode) return;
      const remaining = this.resumeAt - performance.now();
      this.timer = setTimeout(() => { this.timer = null; this.move(1); }, remaining > 0 ? remaining : 5000);
    }
  }
  customElements.define('rr-featured-loop', FeaturedLoop);
}
